import { useState, useRef, forwardRef, useImperativeHandle } from "react";
import { Popover, Whisper } from "rsuite";
import { useAtomValue } from "jotai";
import DisplayRecord from "../DisplayRecord";
import Location, { CountrySymbol, getIsCountry } from "./Location";
import { IsMobileAtom } from "../../../App";
import AppSpinner from "../../../elements/AppSpinner";
import EmptyField from "../EmptyField";

const LocationRecord = (
  {
    field,
    type,
    recordID,
    existingValues,
    inputSize,
    mode,
    tableView,
    modifyTrigger,
    onUpdate,
    submitting,
    error,
    createMode, // createMode - Used for Tables that don't save to API directly (MultiSubitem Tables)
  },
  ref
) => {
  const mobileScreen = useAtomValue(IsMobileAtom);
  const [isEditing, setIsEditing] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);

  const whisperRef = useRef();

  const startEditing = () => {
    whisperRef.current?.open();
  };

  const save = (updatedLocations) => {
    if (createMode) {
      return onUpdate({ payload: updatedLocations });
    }

    if (JSON.stringify(existingValues) === JSON.stringify(updatedLocations)) {
      return;
    }

    const payload = [];

    // Calculate all added / updated records
    for (const loc of updatedLocations) {
      let action = "Add";

      if (Array.isArray(existingValues) && existingValues?.length > 0) {
        const foundLocation = existingValues.find((l) => l.id === loc.id);

        if (foundLocation) {
          if (
            foundLocation &&
            JSON.stringify(foundLocation) === JSON.stringify(loc)
          ) {
            continue;
          }

          action = "Update";
        }
      }

      loc.Address.Address1 = `${loc.Address.StreetNumber} ${loc.Address.Address1}`;

      payload.push({
        action: action,
        payload: loc,
      });
    }

    // Calculate all removed records
    if (Array.isArray(existingValues) && existingValues.length) {
      for (const loc of existingValues) {
        const foundLocation = updatedLocations.find((l) => l.id === loc.id);
        if (foundLocation) continue;
        payload.push({ action: "Remove", payload: loc });
      }
    }

    onUpdate({
      fieldAPIName: field.APIName,
      recordID: recordID,
      payload: payload,
      subitemParent: field?.SubitemParent,
    });
  };

  const handleClose = () => {
    setIsEditing(false);
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      startEditing();
    },
  }));

  return (
    <Whisper
      preventOverflow
      ref={whisperRef}
      placement="autoVertical"
      trigger={isEditing && !optionsOpen ? "click" : "none"}
      onOpen={() => setIsEditing(true)}
      onClose={handleClose}
      speaker={
        <Popover
          className="sidebar-popover p-0 m-0"
          style={{
            width: fullScreen ? 700 : 350,
            maxHeight: "90vh",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Location
            locations={existingValues}
            fullScreen={fullScreen}
            setFullScreen={setFullScreen}
            setOptionsOpen={setOptionsOpen}
            field={field}
            onClose={() => whisperRef?.current?.close()}
            save={save}
            existingValues={existingValues}
          />
        </Popover>
      }
    >
      <div>
        <DisplayRecord
          mode={mode}
          tableView={tableView}
          readonly={field?.ReadOnly ?? false}
          modifyTrigger={modifyTrigger}
          openModify={startEditing}
          isSensitive={field?.Sensitive ?? false}
          displayComponent={(any, innerRef) => (
            <>
              {submitting ? (
                <AppSpinner />
              ) : (
                <div ref={innerRef}>
                  <div
                    className="tw:max-w-92 tw:min-w-5.5 tw:truncate"
                    ref={innerRef}
                  >
                    {Array.isArray(existingValues) &&
                    existingValues?.length > 0 ? (
                      existingValues.map((value, index) => {
                        const isCountry = getIsCountry(value);
                        let icon;

                        const locationText = (
                          <span
                            className={`${
                              mobileScreen || tableView ? "pr-0" : "pr-3"
                            } pl-2 tw:truncate`}
                            key={index}
                          >
                            {value?.Readable}
                          </span>
                        );

                        if (isCountry) {
                          icon = <CountrySymbol location={value} />;
                        } else {
                          icon = (
                            <i
                              className="mdi mdi-map-marker text-dark"
                              style={{
                                lineHeight: 1,
                                fontSize: 18,
                              }}
                            ></i>
                          );
                        }

                        return (
                          <div
                            key={index}
                            className="d-flex align-items-center"
                          >
                            {icon}
                            {locationText}
                          </div>
                        );
                      })
                    ) : (
                      <EmptyField table={tableView} />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        />
      </div>
    </Whisper>
  );
};

export default forwardRef(LocationRecord);
